import React, { useMemo, useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import * as Crypto from 'expo-crypto';
import type { AnalyzedItem } from '../lib/gemini';
import { analyzeProductImage, toAnalyzedItem } from '../lib/backendApi';
import type { RootStackParamList } from '../types/navigation';
import { detectSimilarProducts, type ProductGroup } from '../utils/productGrouping';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../styles/ThemeProvider';

type Props = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'GroupItems'>;
  route: RouteProp<RootStackParamList, 'GroupItems'>;
};

interface GroupState {
  group: ProductGroup;
  merged: boolean;
  reanalyzing: boolean;
  mergedItem?: AnalyzedItem;
}

export default function GroupItemsScreen({ navigation, route }: Props) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        centered: {
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          padding: theme.spacing.xl,
        },
        header: {
          padding: theme.spacing.lg,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        headerTitle: {
          ...theme.typography.h3,
          color: theme.colors.textPrimary,
        },
        headerSubtitle: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.xxs,
        },
        scrollView: {
          flex: 1,
        },
        groupCard: {
          margin: theme.spacing.lg,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
        },
        groupHeader: {
          padding: theme.spacing.md,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        groupTitle: {
          ...theme.typography.lg,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        groupReason: {
          ...theme.typography.xs,
          color: theme.colors.textTertiary,
          marginTop: theme.spacing.xxs,
          letterSpacing: 0,
        },
        imageScroll: {
          padding: theme.spacing.md,
        },
        thumbnailContainer: {
          marginRight: theme.spacing.sm,
        },
        thumbnail: {
          width: 80,
          height: 80,
          borderRadius: theme.radius.radiusSm,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
        },
        thumbnailPlaceholder: {
          backgroundColor: theme.colors.backgroundElevated,
          justifyContent: 'center',
          alignItems: 'center',
        },
        itemDetails: {
          padding: theme.spacing.md,
          borderTopWidth: theme.borders.borderWidth,
          borderTopColor: theme.borders.borderColor,
        },
        detailText: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          marginBottom: theme.spacing.xxs,
        },
        mergedBadge: {
          flexDirection: 'row',
          alignItems: 'center',
          marginTop: theme.spacing.sm,
          gap: theme.spacing.xs,
        },
        mergedText: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.accent,
          letterSpacing: 0,
        },
        actions: {
          flexDirection: 'row',
          padding: theme.spacing.md,
          gap: theme.spacing.sm,
          borderTopWidth: theme.borders.borderWidth,
          borderTopColor: theme.borders.borderColor,
        },
        actionButton: {
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          paddingVertical: theme.spacing.md,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          gap: theme.spacing.xs,
        },
        mergeButton: {
          backgroundColor: theme.colors.accent,
          borderColor: theme.colors.accent,
        },
        mergeButtonText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.backgroundPrimary,
        },
        separateButton: {
          backgroundColor: 'transparent',
        },
        separateButtonText: {
          ...theme.typography.sm,
          color: theme.colors.textPrimary,
        },
        footer: {
          padding: theme.spacing.lg,
          borderTopWidth: theme.borders.borderWidth,
          borderTopColor: theme.borders.borderColor,
        },
        continueButton: {
          backgroundColor: theme.colors.accent,
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.xl,
          borderRadius: theme.radius.radiusMd,
          alignItems: 'center',
        },
        continueButtonText: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.backgroundPrimary,
        },
        loadingText: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.md,
        },
      }),
    [theme],
  );
  const { user } = useAuth();
  const { analyzedItems, imageStoragePaths } = route.params;
  const [groups, setGroups] = useState<GroupState[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [imageUrls, setImageUrls] = useState<{ [path: string]: string }>({});

  useEffect(() => {
    initializeGroups();
    fetchImageUrls();
  }, []);

  const initializeGroups = () => {
    const detectedGroups = detectSimilarProducts(analyzedItems);
    console.log(`[DEBUG] Detected ${detectedGroups.length} potential duplicate groups`);
    
    const groupStates = detectedGroups.map(group => ({
      group,
      merged: false,
      reanalyzing: false,
    }));
    
    setGroups(groupStates);
    setLoading(false);
    
    // If no duplicates detected, proceed directly without showing a screen
    if (detectedGroups.length === 0) {
      proceedWithoutDuplicates();
    }
  };

  const proceedWithoutDuplicates = () => {
    // Navigate directly to AddAnalyzedItem with all items
    const finalImageMap: { [itemIndex: number]: string | string[] } = {};
    analyzedItems.forEach((item, idx) => {
      finalImageMap[idx] = imageStoragePaths[idx];
    });

    navigation.replace('AddAnalyzedItem', {
      items: analyzedItems,
      imageStoragePath: imageStoragePaths[0],
      itemImageMap: finalImageMap,
      multiplePhotosPerItem: true,
    });
  };

  const fetchImageUrls = async () => {
    const urlMap: { [path: string]: string } = {};
    
    for (const path of imageStoragePaths) {
      const { data } = supabase.storage
        .from('item-images')
        .getPublicUrl(path);
      
      if (data?.publicUrl) {
        urlMap[path] = data.publicUrl;
      }
    }
    
    setImageUrls(urlMap);
  };

  const handleMergeGroup = async (groupIndex: number) => {
    const groupState = groups[groupIndex];
    const { indices } = groupState.group;

    setGroups(prev => prev.map((g, i) =>
      i === groupIndex ? { ...g, reanalyzing: true } : g
    ));

    try {
      console.log(`[DEBUG] Re-analyzing ${indices.length} images together via backend`);

      // Collect storage paths for all images in this group
      const groupImagePaths = indices.map(idx => imageStoragePaths[idx]);
      const productId = Crypto.randomUUID();

      // Call backend with multiple image paths — gets full pricing + catalog data
      const backendResponse = await analyzeProductImage(
        groupImagePaths,
        productId,
        false,  // forcePriceDiscovery
        user?.id,
        false,  // isVideo
        false,  // isBase64 — these are storage paths
      );

      if (!backendResponse.success) {
        throw new Error(backendResponse.message || 'Re-analysis failed');
      }

      const mergedItem = toAnalyzedItem(backendResponse);

      console.log('[DEBUG] Backend re-analysis complete:', mergedItem.name);

      setGroups(prev => prev.map((g, i) =>
        i === groupIndex ? {
          ...g,
          merged: true,
          reanalyzing: false,
          mergedItem
        } : g
      ));

    } catch (error) {
      console.error('[ERROR] Failed to re-analyze group:', error);
      Alert.alert('Error', 'Failed to re-analyze images. Please try again.');

      setGroups(prev => prev.map((g, i) =>
        i === groupIndex ? { ...g, reanalyzing: false } : g
      ));
    }
  };

  const handleKeepSeparate = (groupIndex: number) => {
    // Remove this group from the list (keep items separate)
    setGroups(prev => prev.filter((_, i) => i !== groupIndex));
  };

  const handleContinue = () => {
    setProcessing(true);

    // Build final items list
    const finalItems: AnalyzedItem[] = [];
    const finalImageMap: { [itemIndex: number]: string | string[] } = {};
    const processedIndices = new Set<number>();

    // Add merged groups
    groups.forEach(groupState => {
      if (groupState.merged && groupState.mergedItem) {
        const itemIndex = finalItems.length;
        finalItems.push(groupState.mergedItem);
        
        // Map all images from this group to the merged item
        const imagePaths = groupState.group.indices.map(idx => imageStoragePaths[idx]);
        finalImageMap[itemIndex] = imagePaths;
        
        // Mark these indices as processed
        groupState.group.indices.forEach(idx => processedIndices.add(idx));
      }
    });

    // Add non-grouped items (kept separate)
    analyzedItems.forEach((item, idx) => {
      if (!processedIndices.has(idx)) {
        const itemIndex = finalItems.length;
        finalItems.push(item);
        finalImageMap[itemIndex] = imageStoragePaths[idx];
      }
    });

    console.log(`[DEBUG] Final items: ${finalItems.length}, Image map:`, finalImageMap);

    // Navigate to AddAnalyzedItem with grouped results
    navigation.replace('AddAnalyzedItem', {
      items: finalItems,
      imageStoragePath: imageStoragePaths[0],
      itemImageMap: finalImageMap,
      multiplePhotosPerItem: true,
    });
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={theme.colors.accent} />
          <Text style={styles.loadingText}>Detecting similar products...</Text>
        </View>
      </SafeAreaView>
    );
  }

  // If no groups, navigation happens automatically in initializeGroups
  // This return should never be reached, but kept as fallback
  if (groups.length === 0) {
    return null;
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Review Similar Items</Text>
        <Text style={styles.headerSubtitle}>
          We found {groups.length} group{groups.length !== 1 ? 's' : ''} of potentially duplicate items
        </Text>
      </View>

      <ScrollView style={styles.scrollView}>
        {groups.map((groupState, groupIndex) => {
          const { group, merged, reanalyzing, mergedItem } = groupState;
          const displayItem = merged && mergedItem ? mergedItem : group.representativeItem;

          return (
            <View key={groupIndex} style={styles.groupCard}>
              <View style={styles.groupHeader}>
                <Text style={styles.groupTitle}>
                  {displayItem.brand} {displayItem.model || displayItem.name}
                </Text>
                <Text style={styles.groupReason}>{group.reason}</Text>
              </View>

              {/* Image thumbnails */}
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.imageScroll}>
                {group.indices.map((idx) => {
                  const imagePath = imageStoragePaths[idx];
                  const imageUrl = imageUrls[imagePath];
                  
                  return (
                    <View key={idx} style={styles.thumbnailContainer}>
                      {imageUrl ? (
                        <Image source={{ uri: imageUrl }} style={styles.thumbnail} />
                      ) : (
                        <View style={[styles.thumbnail, styles.thumbnailPlaceholder]}>
                          <Ionicons name="image-outline" size={24} color={theme.colors.textTertiary} />
                        </View>
                      )}
                    </View>
                  );
                })}
              </ScrollView>

              {/* Item details */}
              <View style={styles.itemDetails}>
                <Text style={styles.detailText}>
                  Category: {displayItem.category || 'N/A'}
                </Text>
                <Text style={styles.detailText}>
                  Estimated Value: ${displayItem.priceRange?.min || 0} - ${displayItem.priceRange?.max || 0}
                </Text>
                {merged && (
                  <View style={styles.mergedBadge}>
                    <Ionicons name="checkmark-circle" size={16} color={theme.colors.accent} />
                    <Text style={styles.mergedText}>Merged & Re-analyzed</Text>
                  </View>
                )}
              </View>

              {/* Actions */}
              {!merged && (
                <View style={styles.actions}>
                  <TouchableOpacity
                    style={[styles.actionButton, styles.mergeButton]}
                    onPress={() => handleMergeGroup(groupIndex)}
                    disabled={reanalyzing}
                  >
                    {reanalyzing ? (
                      <ActivityIndicator size="small" color={theme.colors.backgroundPrimary} />
                    ) : (
                      <>
                        <Ionicons name="git-merge" size={18} color={theme.colors.backgroundPrimary} />
                        <Text style={styles.mergeButtonText}>Merge & Re-analyze</Text>
                      </>
                    )}
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.actionButton, styles.separateButton]}
                    onPress={() => handleKeepSeparate(groupIndex)}
                    disabled={reanalyzing}
                  >
                    <Text style={styles.separateButtonText}>Keep Separate</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>

      <View style={styles.footer}>
        <TouchableOpacity
          style={styles.continueButton}
          onPress={handleContinue}
          disabled={processing || groups.some(g => g.reanalyzing)}
        >
          <Text style={styles.continueButtonText}>
            {processing ? 'Processing...' : 'Continue'}
          </Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

